import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Button from 'components/v4/Button';
import AGGridBase from './AGGridBase';
import AGGridSearchBar from './AGGridSearchBar';
import CopyCellRenderer from './CustomCells/CopyCellRenderer';
import useSaveTableState from './hooks/useSaveTableState';

// Locale-aware comparator for proper accented character sorting; sorts numerically when both values are numeric
const localeComparator = (valueA, valueB) => {
    const numA = Number(valueA);
    const numB = Number(valueB);
    if (
        valueA != null &&
        valueB != null &&
        valueA !== '' &&
        valueB !== '' &&
        !isNaN(numA) &&
        !isNaN(numB)
    ) {
        return numA - numB;
    }
    const a = valueA != null ? String(valueA) : '';
    const b = valueB != null ? String(valueB) : '';
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
};

const cleanGridState = (state) => {
    if (!state || typeof state !== 'object') return state;

    if (Array.isArray(state)) {
        return state.map((item) => cleanGridState(item));
    }

    const cleaned = {};
    for (const key in state) {
        const value = state[key];
        if (value !== undefined && value !== null) {
            if (typeof value === 'object') {
                const cleanedValue = cleanGridState(value);
                if (
                    Array.isArray(cleanedValue)
                        ? cleanedValue.length > 0
                        : Object.keys(cleanedValue).length > 0
                ) {
                    cleaned[key] = cleanedValue;
                }
            } else {
                cleaned[key] = value;
            }
        }
    }
    return cleaned;
};

// Use AGGridMain to render a majority of the new tables
const AGGridMain = ({
    addButtonAction,
    gridRef,
    tableData,
    addButtonWalkthroughId,
    addButtonText,
    masterDetail,
    isLoading,
    isFetching,
    isRefetching,
    dataSourceKey,
    noSearchBar,
    refetch,
    isRowMaster,
    rowModelType,
    extraComponents,
    titleName,
    rowSelection,
    onRowClicked,
    onSelectionChanged,
    overlayNoRowsTemplate,
    rowGroupButton = false,
    csvHeaders = {}, // Add new prop for custom headers
    isSaveButtonEnabled = false,
    isRefreshDisabled = false, // Optional: disable the refresh icon when there's nothing it could fetch
    stateIdentifier, // Optional prop to override table state key for different views
    sanitiseState, // Optional fn: (state) => state — strip stale col ids before applying saved state
    ...props
}) => {
    const dispatch = useDispatch();
    const [quickFilterText, setQuickFilterText] = useState();
    const [groupingHeader, setGroupingHeader] = useState('never');
    const groupingHeaderToggle = () =>
        setGroupingHeader((prev) => (prev === 'never' ? 'always' : 'never'));

    const settings = useSelector((state) => state.settings, shallowEqual);

    const uiSettings = settings?.uiSettings || settings;
    const tableStateData = uiSettings?.tableState;

    // Same navigation-context values useCustomQuery prepends to every
    // queryKey — switching company/partner/wholesaler changes the query key
    // and triggers a refetch, which should show the overlay like a fresh load.
    const { currentWholesaler, currentPartner, currentCompany } = useSelector(
        (state) => state.navigationLists,
        shallowEqual,
    );

    // Scope isn't prepended by useCustomQuery, but scope-aware tables include it
    // in their own queryKey, so a scope change refetches just the same.
    const currentScope = useSelector((state) => state.login?.currentScope);

    const tableIdentifier = useMemo(() => {
        // Use stateIdentifier if provided, otherwise fall back to titleName
        const baseName = stateIdentifier || titleName;
        if (!baseName) return null;
        const identifier = baseName
            .toLowerCase()
            .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
            .replace(/^[A-Z]/, (chr) => chr.toLowerCase());
        return identifier;
    }, [titleName, stateIdentifier]);

    const { saveState } = useSaveTableState({
        existingSettings: uiSettings || {},
        tableName: tableIdentifier,
    });

    const [initialState, setInitialState] = useState(() => {
        if (tableIdentifier && tableStateData?.[tableIdentifier]) {
            const saved = tableStateData[tableIdentifier];
            return sanitiseState ? sanitiseState(saved) : saved;
        }
        return undefined;
    });

    const hasAppliedSavedState = useRef(!!initialState);
    const [gridVisible, setGridVisible] = useState(true);
    const currentStateRef = useRef(null);
    const [hasChanges, setHasChanges] = useState(false);
    const baselineStateRef = useRef(null);
    const baselineCaptured = useRef(false);
    const initializationComplete = useRef(false);
    const initTimer = useRef(null);
    const previousTableIdentifier = useRef(tableIdentifier);
    const hasLoadedRef = useRef(false);
    const hasObservedFetchRef = useRef(false);

    // Re-clicking the current sidebar item is a deliberate "reset this page"
    // action, which remounts the page and invalidates its queries. Captured at
    // mount so it describes this mount only: without it the reset is invisible
    // whenever the refetch returns the same rows, because the overlay is
    // otherwise suppressed while rows are on screen.
    const location = useLocation();
    const isResetMount = useRef(!!location?.state?.pageReset).current;
    const [isManualRefreshing, setIsManualRefreshing] = useState(false);

    // React Query v4 reports isLoading (status === 'loading') for a *disabled*
    // query as well as an in-flight one, so isLoading alone can't mean "a
    // request is running" — a scope/company-guarded table would spin forever.
    // Prefer the explicit fetch flags, falling back to isLoading for callers
    // that only pass that one.
    const hasFetchFlags =
        isFetching !== undefined || isRefetching !== undefined;
    const isFetchInFlight = hasFetchFlags
        ? !!(isFetching || isRefetching)
        : !!isLoading;

    // Marks the current table view as "loaded" once a fetch has actually been
    // observed to settle, so subsequent background refetches don't re-flash the
    // loading overlay. Waiting for a real fetch — rather than closing the gate
    // the first time isLoading is falsy — keeps the overlay armed for tables
    // whose query starts disabled; those render with every flag false before
    // the request is ever issued.
    useEffect(() => {
        if (isFetchInFlight) {
            hasObservedFetchRef.current = true;
            return;
        }
        if (hasObservedFetchRef.current) {
            hasLoadedRef.current = true;
        }
    }, [isFetchInFlight]);

    const loadingGateKey = `${currentWholesaler}|${currentPartner}|${currentCompany}|${currentScope}|${
        dataSourceKey ?? ''
    }`;

    const [gateKeyAtLastReset, setGateKeyAtLastReset] =
        useState(loadingGateKey);

    if (gateKeyAtLastReset !== loadingGateKey) {
        setGateKeyAtLastReset(loadingGateKey);
        hasLoadedRef.current = false;
        hasObservedFetchRef.current = false;
    }

    // Saved state containing only column widths/order can be applied to an
    // already-mounted grid via the column API, avoiding the header/column
    // flash caused by unmounting and remounting AgGridReact.
    const isOnlyColumnState = useCallback(
        (state) =>
            !!state &&
            Object.keys(state).every(
                (key) => key === 'columnSizing' || key === 'columnOrder',
            ),
        [],
    );

    const applyColumnStateFromSaved = useCallback(
        (state) => {
            const api = gridRef?.current?.api;
            if (!api) return false;

            const orderedColIds = state?.columnOrder?.orderedColIds;
            const sizingModel = state?.columnSizing?.columnSizingModel;
            const sizingById = new Map(
                (sizingModel || []).map((s) => [s.colId, s]),
            );
            const colIds = orderedColIds || sizingModel?.map((s) => s.colId);
            if (!colIds?.length) return false;

            api.applyColumnState({
                state: colIds.map((colId) => {
                    const sizing = sizingById.get(colId);
                    return {
                        colId,
                        ...(sizing?.width !== undefined && {
                            width: sizing.width,
                        }),
                        ...(sizing?.flex !== undefined && {
                            flex: sizing.flex,
                        }),
                    };
                }),
                applyOrder: !!orderedColIds,
            });
            return true;
        },
        [gridRef],
    );

    // Reset state tracking when switching between different table views
    useEffect(() => {
        if (previousTableIdentifier.current !== tableIdentifier) {
            previousTableIdentifier.current = tableIdentifier;
            hasAppliedSavedState.current = false;
            initializationComplete.current = false;
            baselineCaptured.current = false;
            hasLoadedRef.current = false;
            hasObservedFetchRef.current = false;
            setHasChanges(false);

            if (initTimer.current) {
                clearTimeout(initTimer.current);
                initTimer.current = null;
            }

            // Load the new saved state or clear it
            const raw = tableIdentifier && tableStateData?.[tableIdentifier];
            const newSavedState =
                raw && sanitiseState ? sanitiseState(raw) : raw;
            setInitialState(newSavedState || undefined);

            // Remount grid to apply new state
            setGridVisible(false);
            setTimeout(() => {
                setGridVisible(true);
                hasAppliedSavedState.current = !!newSavedState;
            }, 0);
        }
    }, [tableIdentifier, tableStateData, sanitiseState]);

    useEffect(() => {
        if (hasAppliedSavedState.current) return;
        if (!tableIdentifier || !tableStateData?.[tableIdentifier]) return;

        const raw = tableStateData[tableIdentifier];
        const savedState = sanitiseState ? sanitiseState(raw) : raw;
        if (savedState) {
            hasAppliedSavedState.current = true;
            setInitialState(savedState);

            if (
                isOnlyColumnState(savedState) &&
                applyColumnStateFromSaved(savedState)
            ) {
                return;
            }

            setGridVisible(false);
            setTimeout(() => setGridVisible(true), 0);
        }
    }, [
        tableIdentifier,
        tableStateData,
        isOnlyColumnState,
        applyColumnStateFromSaved,
        sanitiseState,
    ]);

    const onStateUpdated = useCallback((params) => {
        currentStateRef.current = params.state;

        if (!initializationComplete.current) {
            if (initTimer.current) {
                clearTimeout(initTimer.current);
            }

            initTimer.current = setTimeout(() => {
                initializationComplete.current = true;
                baselineStateRef.current = cleanGridState(params.state);
                baselineCaptured.current = true;
            }, 1000);

            return;
        }

        if (!baselineCaptured.current) {
            return;
        }

        const cleanedCurrentState = cleanGridState(params.state);
        const cleanedBaselineState = cleanGridState(baselineStateRef.current);
        const stateChanged =
            JSON.stringify(cleanedCurrentState) !==
            JSON.stringify(cleanedBaselineState);
        setHasChanges(stateChanged);
    }, []);

    useEffect(() => {
        return () => {
            if (initTimer.current) {
                clearTimeout(initTimer.current);
            }
        };
    }, []);

    const onGridPreDestroyed = useCallback((params) => {
        const { state } = params;
        currentStateRef.current = state;
    }, []);

    const handleSaveState = useCallback(() => {
        const currentState = gridRef?.current?.api?.getState();
        if (currentState && tableIdentifier) {
            const cleanedState = cleanGridState(currentState);
            saveState(cleanedState);
            dispatch({
                type: 'CHANGE_TABLE_STATE',
                payload: { [tableIdentifier]: cleanedState },
            });
            hasAppliedSavedState.current = true;
            baselineStateRef.current = cleanedState;
            baselineCaptured.current = true;
            initializationComplete.current = true;
            setHasChanges(false);
        } else {
            console.warn('No grid state available to save');
        }
    }, [gridRef, saveState, dispatch, tableIdentifier]);

    useEffect(() => {
        if (gridRef.current?.api && !hasAppliedSavedState.current) {
            gridRef.current.api.sizeColumnsToFit();
        }
    }, [tableData, gridRef]);

    const onGridReady = useCallback(
        (params) => {
            if (props.onGridReady) {
                props.onGridReady(params);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.onGridReady],
    );
    // Clear button for filters
    const postProcessPopup = useCallback(
        (params) => {
            const { type, ePopup, column } = params;
            if (type === 'columnFilter' && column && gridRef?.current?.api) {
                // Check if we've already added a clear button
                if (ePopup.querySelector('.ag-custom-clear-btn')) return;

                const clearBtn = document.createElement('button');
                clearBtn.className = 'ag-custom-clear-btn';
                clearBtn.textContent = 'Reset';
                clearBtn.style.cssText = `
                    background-color: #007bff;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    padding: 6px 16px;
                    cursor: pointer;
                    font-size: 13px;
                    font-weight: 500;
                    margin: 8px;
                    width: calc(100% - 16px);
                `;
                clearBtn.onmouseover = () =>
                    (clearBtn.style.backgroundColor = '#0056b3');
                clearBtn.onmouseout = () =>
                    (clearBtn.style.backgroundColor = '#007bff');
                clearBtn.onclick = () => {
                    gridRef.current.api.setColumnFilterModel(
                        column.getColId(),
                        null,
                    );
                    gridRef.current.api.onFilterChanged();
                };

                ePopup.appendChild(clearBtn);
            }
        },
        [gridRef],
    );

    const defaultColDef = useMemo(() => {
        return {
            sortable: true,
            enableRowGroup: true,
            filter: 'agMultiColumnFilter',
            filterParams: {
                closeOnApply: false,
                filters: [
                    {
                        filter: 'agTextColumnFilter',
                        filterParams: {
                            closeOnApply: false,
                            applyMiniFilterWhileTyping: true,
                        },
                    },
                    {
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            closeOnApply: false,
                            applyMiniFilterWhileTyping: true,
                            suppressMiniFilter: true,
                        },
                    },
                ],
            },
            resizable: true,
            comparator: localeComparator,
            suppressSizeToFit: true,
            flex: 1,
            minWidth: 100,
            suppressHeaderMenuButton: true,
            cellStyle: { display: 'flex', alignItems: 'center' },
            getQuickFilterText: (params) => params.value,
        };
    }, []);

    const processCellCallback = (params) => {
        let value = params.value;

        if (typeof value === 'string') {
            value = value.replace(/\\x09|\\x0D/g, '');
        }
        if (typeof value === 'string' && /^[=+\-@]/.test(value[0])) {
            return `'${value}`;
        }

        return value;
    };

    // Memoize the callback for stability if it's used in other hooks/props
    const memoizedProcessCellCallback = useCallback(processCellCallback, []);

    const processHeaderCallback = useCallback(
        (params) => {
            // Get the column definition
            const colDef = params.column.colDef;

            // First try to get the header from csvHeaders if it exists
            if (
                csvHeaders &&
                typeof colDef.field === 'string' &&
                csvHeaders[colDef.field]
            ) {
                return csvHeaders[colDef.field];
            }

            // Otherwise use the headerName from colDef, or as last resort the field name
            return colDef.headerName || colDef.field || '';
        },
        [csvHeaders],
    );

    const defaultCsvExportParams = useMemo(() => {
        return {
            processCellCallback: memoizedProcessCellCallback,
            processHeaderCallback,
            fileName: titleName ? `${titleName}.csv` : 'export.csv',
            skipHeader: false,
            allColumns: false,
        };
    }, [titleName, memoizedProcessCellCallback, processHeaderCallback]);

    const defaultExcelExportParams = useMemo(() => {
        return {
            processCellCallback: memoizedProcessCellCallback,
            processHeaderCallback,
            fileName: titleName ? `${titleName}.xlsx` : 'export.xlsx',
            sheetName: titleName || 'Data',
            skipHeader: false,
            allColumns: false,
            exportAsExcelTable: true,
            columnWidth: 120,
        };
    }, [titleName, memoizedProcessCellCallback, processHeaderCallback]);

    const handleExportCsv = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.exportDataAsCsv(defaultCsvExportParams);
        } else {
            console.warn('AG Grid API not available for CSV export.');
            // Optionally, provide user feedback here, e.g., via a toast notification
        }
    }, [gridRef, defaultCsvExportParams]); // gridRef itself is stable, defaultCsvExportParams is memoized

    // Always shows the loading overlay for a manual refresh, regardless of how
    // the page computes isLoading (fixes pages whose isLoading never re-arms after first load)
    const handleRefresh = useCallback(async () => {
        setIsManualRefreshing(true);
        try {
            await refetch?.();
        } finally {
            setIsManualRefreshing(false);
        }
    }, [refetch]);

    // The overlay belongs on screen only while a request is in flight and there
    // is nothing to show yet. Requiring an empty grid is what removes the
    // flicker: clicking the same menu item remounts the page via /reload, and
    // React Query serves the cached rows immediately while it refetches in the
    // background — painting the overlay over rows that are already visible is
    // exactly what read as a flash. It also covers pages that mirror query data
    // into local state, whose grid is briefly empty on remount and used to show
    // a bogus "No <X> Found" message instead of a spinner. A manual refresh is
    // an explicit user action, so it always shows the overlay — as is a sidebar
    // reset, which is why isResetMount waives the empty-grid requirement for
    // the one fetch that follows it.
    const hasRows = (tableData?.data?.length ?? 0) > 0;
    const effectiveLoading =
        isManualRefreshing ||
        (!hasLoadedRef.current &&
            isFetchInFlight &&
            (!hasRows || isResetMount));

    const hasSavedColumnState = !!(
        initialState?.columnSizing || initialState?.columnOrder
    );
    const autoSizeStrategy = useMemo(() => {
        if (hasSavedColumnState) {
            return undefined;
        }
        return {
            type: 'fitGridWidth',
        };
    }, [hasSavedColumnState]);

    const autoGroupColumnDef = useMemo(() => {
        return {
            minWidth: 200,
        };
    }, []);

    return (
        <>
            {addButtonAction && (
                <Button
                    onClick={addButtonAction}
                    data-walkthroughid={addButtonWalkthroughId}>
                    {`+ ${addButtonText}`}
                </Button>
            )}
            {!noSearchBar && (
                <AGGridSearchBar
                    quickFilterText={quickFilterText}
                    setQuickFilterText={setQuickFilterText}
                    refetch={handleRefresh}
                    extraComponents={extraComponents}
                    onExportCsv={handleExportCsv}
                    rowGroupButton={rowGroupButton}
                    groupingHeaderToggle={groupingHeaderToggle}
                    saveButton={handleSaveState}
                    isSaveButtonEnabled={isSaveButtonEnabled}
                    hasChanges={hasChanges}
                    isRefreshDisabled={isRefreshDisabled}
                />
            )}
            {gridVisible && (
                <AGGridBase
                    gridRef={gridRef}
                    groupingHeader={groupingHeader}
                    tableData={tableData}
                    addButtonAction={addButtonAction}
                    addButtonWalkthroughId={addButtonWalkthroughId}
                    addButtonText={addButtonText}
                    masterDetail={masterDetail}
                    isRowMaster={isRowMaster}
                    isLoading={effectiveLoading}
                    quickFilterText={quickFilterText}
                    defaultColDef={defaultColDef}
                    autoGroupColumnDef={autoGroupColumnDef}
                    customCellRenderer={CopyCellRenderer}
                    defaultCsvExportParams={defaultCsvExportParams}
                    defaultExcelExportParams={defaultExcelExportParams}
                    autoSizeStrategy={autoSizeStrategy}
                    rowModelType={rowModelType}
                    rowSelection={rowSelection}
                    onRowClicked={onRowClicked}
                    onSelectionChanged={onSelectionChanged}
                    overlayNoRowsTemplate={overlayNoRowsTemplate}
                    initialState={initialState}
                    onGridReady={onGridReady}
                    onStateUpdated={onStateUpdated}
                    onGridPreDestroyed={onGridPreDestroyed}
                    postProcessPopup={postProcessPopup}
                    {...props}
                />
            )}
        </>
    );
};

export default AGGridMain;
